import React, { useState, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import ExcelJS from 'exceljs';
import moment from 'moment';
import qs from 'qs';

import {
  getPaymentList,
  getPaymentSummary,
  getPaymentSummaryDetail,
  getOrderPaymentLines,
  getOrderRefundLines,
  cancelProductRefund,
} from '../../../api/payment';
import { errorMessages } from '../../../utils/methods';
import ManualRefundModalV2 from '../../manualSales/manualModal/manualRefundModalV2';
import ManualDetailModalV2 from '../../manualSales/manualModal/manualDetailModalV2';
import ProductModalV2 from '../../manualSales/manualModal/productModalV2';

import SalesManagPeriodBar from './SalesManagPeriodBar';
import SalesManagFilterPanel from './SalesManagFilterPanel';
import SalesManagDashboard from './SalesManagDashboard';
import SalesManagDataTable from './SalesManagDataTable';
import { isMixedSplitRow, splitRowKey, renderSplitLineDetail } from './salesManagTableConfigs';
import useUserAuth from '../../../utils/useUserAuth';
import { VISIT_PATH_LABELS, VISIT_PATH_OPTIONS } from '../../../constants/visitPath';
import { INITIAL_FILTERS, buildFilterQuery, clearFilterField } from './salesFilterSchema';
import { LoadingScreen, PageContainer, FilterDashboardRow } from './SalesManagTab1Styled';

// 테이블 상단 탭. 그룹 탭은 /payments/summary(groupBy), 환불/미수금은 /payments(filterType).
const FIRST_TAB = '전체 내역';
const TAB_TO_GROUPBY = {
  '상품별 내역': 'PRODUCT',
  '상품구분별 내역': 'PRODUCT_TYPE',
  '종목별 내역': 'SPORT_TYPE',
  '결제수단별 내역': 'PAYMENT_METHOD',
};
const TAB_TO_FILTER = {
  '환불 내역': 'REFUND',
  '미수금 내역': 'OUTSTANDING',
};
// 양수금 내역 탭은 filterType 이 아니라 searchPayType=['transfer_fee'] 로 거른다(백엔드 list/count/total 동일 갈래).
const TAB_TO_PAY_TYPE = {
  '양수금 내역': ['transfer_fee'],
};

// 일일/주간/월간 탭과 기준일로 조회 날짜 범위를 계산한다.
const computePeriodRange = (tab, date) => {
  const d = moment(date);
  if (tab === 'week') {
    return {
      from: d.clone().startOf('isoWeek').format('YYYY-MM-DD'),
      to: d.clone().endOf('isoWeek').format('YYYY-MM-DD'),
    };
  }
  if (tab === 'month') {
    return {
      from: d.clone().startOf('month').format('YYYY-MM-DD'),
      to: d.clone().endOf('month').format('YYYY-MM-DD'),
    };
  }
  return { from: d.format('YYYY-MM-DD'), to: d.format('YYYY-MM-DD') };
};

// PRODUCT_TYPE summary 그룹에서 양수금(양도수수료) 그룹의 paid 합을 추출한다.
// 백엔드가 payTypeName='양수금' 그룹을 groupKey='양수금'으로 내려준다(별도 API 없음).
const extractTransferFeeTotal = summaryData =>
  Number((summaryData?.groups || []).find(g => g.groupKey === '양수금' || g.groupValue === '양수금')?.paid) || 0;

// 요약 블록용 상품군별 매출/환불 집계.
// 이원화: 환불건은 결제행(PAY)+환불행(REFUND) 2행. 매출은 결제행, 환불은 환불행에서만 집계 (이중계상 방지)
const aggregateCategoryTotals = rows => {
  const totals = {
    ticketSales: 0,
    passSales: 0,
    productSales: 0,
    ptSales: 0,
    pilatesSales: 0,
    trialSales: 0,
    ticketRefunds: 0,
    passRefunds: 0,
    productRefunds: 0,
    ptRefunds: 0,
    pilatesRefunds: 0,
    trialRefunds: 0,
  };

  rows.forEach(row => {
    const payType = row.payType || row.payTypeName || '';
    const isRefundRow = row.rowType === 'REFUND';
    const paymentAmount = isRefundRow ? 0 : row.finalPrice || 0;
    const rowRefund = isRefundRow ? Math.abs(row.refundAmount || 0) : 0;

    const isDayPass = row.usagePeriodType === 'DAY' && Number(row.usagePeriod) === 1;
    const isTrial = payType === 'TRIAL_TICKET' || payType === '일일권';

    if (isTrial) {
      totals.trialSales += paymentAmount;
      totals.trialRefunds += rowRefund;
    } else if ((payType === 'TICKET' || payType === '수강권') && !isDayPass) {
      const ticketName = (row.courseTicketName || '').toLowerCase();
      if (ticketName.includes('pt') || ticketName.includes('개인')) {
        totals.ptSales += paymentAmount;
        totals.ptRefunds += rowRefund;
      } else if (ticketName.includes('필라') || ticketName.includes('그룹')) {
        totals.pilatesSales += paymentAmount;
        totals.pilatesRefunds += rowRefund;
      } else {
        totals.ticketSales += paymentAmount;
        totals.ticketRefunds += rowRefund;
      }
    } else if (payType === 'PASS' || payType === '이용권' || isDayPass) {
      totals.passSales += paymentAmount;
      totals.passRefunds += rowRefund;
    } else if (payType === 'PRODUCT' || payType === '상품') {
      totals.productSales += paymentAmount;
      totals.productRefunds += rowRefund;
    }
  });

  return totals;
};

const SalesManagTab1 = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.user);
  const canPaymentDetailEdit = useUserAuth('SALES_PAYMENT_DETAIL_EDIT');
  const canContractEdit = useUserAuth('SALES_CONTRACT_EDIT');

  const today = moment().format('YYYY-MM-DD');
  const initialQuery = {
    page: 1,
    limit: 10,
    headBranchId: user.headBranch?.headBranchId,
    branchId: user.branch?.branchId,
    paymentDateFrom: today,
    paymentDateTo: today,
    paymentMethod: null,
    paymentStatus: null,
    searchPayType: ['all'],
  };

  const [payments, setPayments] = useState([]); // 평면 목록 (전체/환불/미수금)
  const [summary, setSummary] = useState(null); // 그룹 집계 (상품별/상품구분별/종목별/결제수단별)
  const [activeTab, setActiveTab] = useState(FIRST_TAB);
  const [paymentTotal, setPaymentTotal] = useState(null); // 대시보드 합계
  const [dashboardCount, setDashboardCount] = useState(0); // 대시보드 결제 건수
  const [transferFeeTotal, setTransferFeeTotal] = useState(0); // 대시보드 양도수수료(양수금 paid) 합계
  const [query, setQuery] = useState(initialQuery);
  const [appliedQuery, setAppliedQuery] = useState(initialQuery); // 엑셀 다운로드용 실제 적용 쿼리

  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  // 필터(pending) + 적용본(applied) + 기간 바 상태.
  // 검색 버튼 방식이라 "바꿨는데 아직 안 눌렀다"를 화면이 알려줘야 해서 둘을 따로 둔다.
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [periodTab, setPeriodTab] = useState('day');
  const [refDate, setRefDate] = useState(moment());
  // 달력·직접 입력으로 집은 임의 구간인지. 이때는 일일/주간/월간 어느 쪽도 선택 상태가 아니다.
  const [customRange, setCustomRange] = useState(false);

  // 모달 상태
  const [selectedTicketData, setSelectedTicketData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // 탭에 따라 집계(summary) 또는 평면 목록(list, filterType)을 조회한다.
  const loadData = async (nextQuery, tab) => {
    setTableLoading(true);
    if (!user.headBranch?.headBranchId || (user.centerType === 'BRANCH' && !user.branch?.branchId)) {
      toast.warning(t('지점 정보가 없습니다'));
      setPayments({ content: [], totalElements: 0, size: 10 });
      setSummary({ groups: [] });
      setPaymentTotal({
        totalPaymentRegularAmount: 0,
        totalPaidAmount: 0,
        totalPaymentDiscountAmount: 0,
        totalOutstandingAmount: 0,
        totalRefundAmount: 0,
      });
      setDashboardCount(0);
      setTransferFeeTotal(0);
      setLoading(false);
      setTableLoading(false);
      return;
    }

    const groupBy = TAB_TO_GROUPBY[tab];
    try {
      // 대시보드 양도수수료 카드는 totalPayMap 에 FEE 필드가 없어 PRODUCT_TYPE summary 의 '양수금' paid 로 채운다.
      // 활성 그룹탭이 이미 PRODUCT_TYPE 이면 그 응답을 재사용하고, 아니면 한 번 별도 조회한다(백엔드 추가 없음).
      // 양도수수료 카드용 보조 집계. 실패해도 메인 목록/대시보드가 함께 무너지지 않도록 격리한다
      // (예: /payments/summary 일시 5xx/타임아웃 시에도 목록·요약은 정상 표시. extractTransferFeeTotal(null)=0).
      const feeSummaryPromise =
        groupBy === 'PRODUCT_TYPE'
          ? null
          : getPaymentSummary(qs.stringify({ ...nextQuery, groupBy: 'PRODUCT_TYPE' }), user.token).catch(() => null);

      if (groupBy) {
        // 그룹탭은 표는 집계(groups)로, 대시보드 카드는 일반탭과 동일한 totalPayMap 으로 채운다.
        // (그룹 집계엔 신규/재등록·체험권·순매출·직전대비 값이 없어 totalPayMap 을 함께 받아온다.)
        const [data, listData, feeSummary] = await Promise.all([
          getPaymentSummary(qs.stringify({ ...nextQuery, groupBy }), user.token),
          getPaymentList(qs.stringify(nextQuery), user.token),
          feeSummaryPromise,
        ]);
        setSummary(data);
        setPaymentTotal(listData.totalPayMap);
        setDashboardCount(listData.totalElements || 0);
        setTransferFeeTotal(extractTransferFeeTotal(groupBy === 'PRODUCT_TYPE' ? data : feeSummary));
      } else {
        const filterType = TAB_TO_FILTER[tab];
        const payTypeOverride = TAB_TO_PAY_TYPE[tab];
        // 양수금 탭은 searchPayType 만 덮어쓴다(filterType 없음). 그 외(전체/환불/미수금)는 filterType 분기.
        const listQuery = payTypeOverride
          ? { ...nextQuery, searchPayType: payTypeOverride }
          : filterType
            ? { ...nextQuery, filterType }
            : nextQuery;
        const queryString = qs.stringify(listQuery);
        const [data, feeSummary] = await Promise.all([getPaymentList(queryString, user.token), feeSummaryPromise]);
        setPayments(data);
        setPaymentTotal(data.totalPayMap);
        setDashboardCount(data.totalElements || 0);
        setTransferFeeTotal(extractTransferFeeTotal(feeSummary));
      }
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  // 그룹 펼침: 해당 그룹(groupBy + groupValue)에 속한 개별 결제 내역을 lazy 조회한다.
  // 집계와 동일한 적용 쿼리(기간/지점/결제수단 등)에 groupBy/groupValue 만 덧붙인다.
  // 복합 결제/환불 행 갈매기 펼침(#49) — 결제행은 수납 라인, 환불행은 환불 라인.
  const allTabExpansion = {
    isExpandable: isMixedSplitRow,
    getKey: splitRowKey,
    fetch: row =>
      (row.rowType === 'REFUND' ? getOrderRefundLines : getOrderPaymentLines)(row.memberOrderId, user.token),
    renderDetail: renderSplitLineDetail,
  };

  const handleFetchGroupDetail = async (groupBy, groupValue) => {
    const queryString = qs.stringify({ ...appliedQuery, groupBy, groupValue });
    const data = await getPaymentSummaryDetail(queryString, user.token);
    return data?.content || [];
  };

  const getPaymentListEvent = async queries => {
    const mergedQuery = { ...query, ...queries };
    setQuery(mergedQuery);
    setAppliedQuery(mergedQuery);
    await loadData(mergedQuery, activeTab);
  };

  const fetchData = async queryParam => {
    setAppliedQuery(queryParam);
    await loadData(queryParam, activeTab);
  };

  // 탭 변경 시 page=1로 리셋 후 해당 탭 데이터 조회
  const handleTabChange = tab => {
    setActiveTab(tab);
    const merged = { ...query, page: 1 };
    setQuery(merged);
    setAppliedQuery(merged);
    loadData(merged, tab);
  };

  // ── 필터 ────────────────────────────────
  // 조건을 적용해 조회한다. 다중선택은 배열 그대로 실어 qs 가 key[0], key[1] 로 직렬화한다.
  // 구독권 구분(subscriptionType): PAID=구독 결제완료만, SCHEDULED=구독 결제예정만, 미선택=기존 전체목록.
  const applyFilters = nextFilters => {
    const merged = { ...query, page: 1, ...buildFilterQuery(nextFilters) };
    setAppliedFilters(nextFilters);
    setQuery(merged);
    fetchData(merged);
  };

  const handleSearch = () => applyFilters(filters);

  // 요약칩 × — 지우면 즉시 적용본까지 반영한다(다시 검색을 누르지 않아도 되게).
  const handleRemoveToken = fieldKey => {
    const nextFilters = clearFilterField(filters, fieldKey);
    setFilters(nextFilters);
    applyFilters(clearFilterField(appliedFilters, fieldKey));
  };

  // 검색조건 초기화
  const handleReset = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setPeriodTab('day');
    setRefDate(moment());
    setCustomRange(false);
    setQuery(initialQuery);
    fetchData(initialQuery);
  };

  // ── 기간 바 ──────────────────────────────
  const applyRange = (from, to) => {
    const merged = { ...query, paymentDateFrom: from, paymentDateTo: to, page: 1 };
    setQuery(merged);
    fetchData(merged);
  };

  const applyPeriod = (tab, date) => {
    const { from, to } = computePeriodRange(tab, date);
    applyRange(from, to);
  };

  const handlePeriodTabChange = newTab => {
    setPeriodTab(newTab);
    setCustomRange(false);
    applyPeriod(newTab, refDate);
  };

  const handleRefDateChange = newDate => {
    setRefDate(newDate);
    setCustomRange(false);
    applyPeriod(periodTab, newDate);
  };

  // 달력·직접 입력으로 임의 구간을 집으면 기준일도 시작일로 옮겨 이후 조작이 이어지게 한다.
  const handleRangeChange = (from, to) => {
    setRefDate(moment(from));
    setCustomRange(true);
    applyRange(from, to);
  };

  // ── 페이지네이션 / 페이지당 개수 ──────────
  const handleChangePage = (event, value) => {
    setQuery({ ...query, page: value });
    getPaymentListEvent({ ...query, page: value });
  };

  const changeLimit = event => {
    setQuery({ ...query, limit: event.target.value, page: 1 });
    getPaymentListEvent({ ...query, limit: event.target.value, page: 1 });
  };

  // ── 모달 ────────────────────────────────
  const handleCancelClick = item => {
    if (!canContractEdit) return;
    const memberOrderIdStr = String(item.memberOrderId);
    const lastPaidMemberOrderIdStr = String(item.lastPaidMemberOrderId);

    setSelectedTicketData({
      memberOrderId: item.memberOrderId,
      memberId: item.memberId,
      courseTicketName: item.courseTicketName,
      payType: item.payType,
      finalPrice: item.finalPrice,
      ticketId: item.ticketId || item.courseTicketId,
      usedCount: item.usedCount || 0,
      outstandingAmount: item.outstandingAmount,
      lastPaidMemberOrderId: item.lastPaidMemberOrderId,
      totalOrderPaymentAmount: item.totalOrderPaymentAmount,
      isChangeDisabled: memberOrderIdStr !== lastPaidMemberOrderIdStr || item.payType === 'PRODUCT',
    });
    setIsModalOpen(true);
  };

  const handleDetailClick = (item, updateType) => {
    const memberOrderIdStr = String(item.memberOrderId);
    const lastPaidMemberOrderIdStr = String(item.lastPaidMemberOrderId);

    setSelectedTicketData({
      memberOrderId: item.memberOrderId,
      memberId: item.memberId,
      updateType: updateType,
      payType: item.payType,
      courseTicketName: item.courseTicketName,
      finalPrice: item.finalPrice,
      ticketId: item.ticketId || item.courseTicketId,
      usedCount: item.usedCount || 0,
      outstandingAmount: item.outstandingAmount,
      lastPaidMemberOrderId: item.lastPaidMemberOrderId,
      totalOrderPaymentAmount: item.totalOrderPaymentAmount,
      isChangeDisabled: memberOrderIdStr !== lastPaidMemberOrderIdStr || item.payType === 'PRODUCT',
      subType: item.subType,
    });
    setIsDetailModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTicketData(null);
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedTicketData(null);
  };

  // ── 환불취소 (환불 행 '환불취소' 버튼 → 확인 다이얼로그 + 사유) ──
  const [reversalTarget, setReversalTarget] = useState(null);
  const [reversalReason, setReversalReason] = useState('');
  const [reversalSubmitting, setReversalSubmitting] = useState(false);
  const handleRefundReversalClick = row => {
    setReversalReason('');
    setReversalTarget(row);
  };
  const handleReversalClose = () => {
    setReversalTarget(null);
    setReversalReason('');
  };
  const handleReversalConfirm = async () => {
    if (!reversalTarget) return;
    setReversalSubmitting(true);
    try {
      await cancelProductRefund(
        {
          memberOrderId: reversalTarget.memberOrderId,
          // 되돌리는 작업자 — 원 주문 담당자(registeredBy), 없으면 로그인 사용자
          registeredBy: reversalTarget.registeredBy || user.userId,
          ...(reversalReason.trim() && { reason: reversalReason.trim() }),
        },
        user.token,
      );
      toast.success('환불 취소가 완료되었습니다');
      handleReversalClose();
      getPaymentListEvent(appliedQuery);
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setReversalSubmitting(false);
    }
  };

  // ── 엑셀 다운로드 (기존 로직 그대로 유지) ──
  const exportToExcel = async () => {
    setExcelLoading(true);
    try {
      const exportQuery = {
        ...appliedQuery,
        page: 1,
        limit: 10000,
      };
      const queryString = qs.stringify(exportQuery);

      // 요약 블록은 조회 기간을 그대로 따른다. 하루 조회면 '일일 영업 매출',
      // 여러 날이면 '기간 영업 매출' + 기간 표기 (기간 합계를 하루치로 적어 오해를 사던 문제).
      const periodFromDate = appliedQuery.paymentDateFrom || moment().format('YYYY-MM-DD');
      const periodToDate = appliedQuery.paymentDateTo || periodFromDate;
      const isSingleDay = periodFromDate === periodToDate;
      const isSingleMonth = moment(periodFromDate).isSame(moment(periodToDate), 'month');
      // 조회 기간이 그 달 전체면(월간 조회) 당월 합계가 좌측 블록과 똑같아서 겹친다. 그때만 뺀다.
      const monthStart = moment(periodFromDate).startOf('month').format('YYYY-MM-DD');
      const monthEnd = moment(periodFromDate).endOf('month').format('YYYY-MM-DD');
      const isWholeMonth = periodFromDate === monthStart && periodToDate === monthEnd;
      const needsMonthlyBlock = isSingleMonth && !isWholeMonth;

      let allData = [];
      let exportPaymentTotal = null;
      let monthlyData = [];
      let monthlyPaymentTotal = null;

      const expectedCount = payments?.totalElements || 0;

      try {
        toast.info(`엑셀 데이터를 불러오는 중... (총 ${expectedCount}건)`);
        const response = await getPaymentList(queryString, user.token);
        allData = response?.content || [];
        exportPaymentTotal = response?.totalPayMap || null;

        // 당월 합계 블록을 그릴 때만 그 달 전체를 한 번 더 조회한다.
        if (needsMonthlyBlock) {
          const monthlyQueryString = qs.stringify({
            ...appliedQuery,
            paymentDateFrom: monthStart,
            paymentDateTo: monthEnd,
            page: 1,
            limit: 10000,
          });
          const monthlyResponse = await getPaymentList(monthlyQueryString, user.token);
          monthlyData = monthlyResponse?.content || [];
          monthlyPaymentTotal = monthlyResponse?.totalPayMap || null;
        }

        if (allData.length !== expectedCount && expectedCount > 0) {
          console.warn(`데이터 건수 불일치: 테이블 ${expectedCount}건, 엑셀 ${allData.length}건`);
        }
      } catch (error) {
        toast.error(errorMessages(error));
        return;
      }

      if (allData.length === 0) {
        toast.warning('다운로드할 데이터가 없습니다.');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('매출현황', {
        pageSetup: {
          orientation: 'landscape',
          paperSize: 9,
          horizontalCentered: true,
          margins: { left: 0.3, right: 0.3, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
          printTitlesRow: '1:1',
        },
      });

      const rowColors = {
        odd: 'FFE2EFDA',
        even: 'FFF5FBF3',
      };

      const headerStyle = {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFBFC' } },
        font: { bold: true, size: 12 },
        border: {
          top: { style: 'thin', color: { argb: 'FFDDDDDD' } },
          left: { style: 'thin', color: { argb: 'FFDDDDDD' } },
          bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } },
          right: { style: 'thin', color: { argb: 'FFDDDDDD' } },
        },
        alignment: { horizontal: 'center', vertical: 'middle' },
      };

      const getProductPeriod = row => {
        if (row.periodValue != null && row.periodType) {
          const unit = row.periodType === 'MONTH' ? '개월' : '일';
          return `${row.periodValue}${unit}`;
        }
        if (row.activeMonths != null && Number(row.activeMonths) > 0) return `${row.activeMonths}개월`;
        if (row.activeDays != null && Number(row.activeDays) > 0) return `${row.activeDays}일`;
        if (row.totalMonths != null && Number(row.totalMonths) > 0) return `${row.totalMonths}개월`;
        if (row.totalDays != null && Number(row.totalDays) > 0) return `${row.totalDays}일`;
        if (row.validMonths != null && Number(row.validMonths) > 0) return `${row.validMonths}개월`;
        if (row.validDays != null && Number(row.validDays) > 0) return `${row.validDays}일`;
        if (row.startDate && row.expireDate) {
          const start = moment(row.startDate);
          const end = moment(row.expireDate);
          if (start.isValid() && end.isValid()) {
            const months = end.diff(start, 'months');
            const days = end.diff(start, 'days') + 1;
            if (months >= 1) return `${months}개월`;
            if (days > 0) return `${days}일`;
          }
        }
        return '';
      };

      const formatProductName = row => {
        const period = getProductPeriod(row);
        const name = row.courseTicketName || '';
        return period ? `${name} (${period})` : name;
      };

      const cellBorder = {
        top: { style: 'thin', color: { argb: 'FFDDDDDD' } },
        left: { style: 'thin', color: { argb: 'FFDDDDDD' } },
        bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } },
        right: { style: 'thin', color: { argb: 'FFDDDDDD' } },
      };

      worksheet.columns = [
        { header: '구분', key: 'payTypeName' },
        { header: '회원명', key: 'memberName' },
        { header: '거래일시', key: 'paidDate' },
        { header: '품 목', key: 'courseTicketName' },
        { header: '분류', key: 'category' },
        { header: '등록일자', key: 'regDate' },
        { header: '현금', key: 'cashAmount' },
        { header: '계좌이체', key: 'transferAmount' },
        { header: '카드', key: 'cardAmount' },
        { header: '미수금', key: 'outstandingAmount' },
        { header: '휴대폰번호', key: 'phoneNumber' },
        { header: '결제수단', key: 'paymentMethod' },
        { header: '결제유형', key: 'paymentProviderName' },
        { header: '특이사항', key: 'memo' },
      ];

      const getTextWidth = text => {
        if (!text) return 0;
        const str = String(text);
        let width = 0;
        for (let i = 0; i < str.length; i++) {
          const code = str.charCodeAt(i);
          if (
            (code >= 0x1100 && code <= 0x11ff) ||
            (code >= 0x3000 && code <= 0x9fff) ||
            (code >= 0xac00 && code <= 0xd7af) ||
            (code >= 0xff00 && code <= 0xffef)
          ) {
            width += 2;
          } else {
            width += 1;
          }
        }
        return width;
      };

      const columnWidths = worksheet.columns.map(col => getTextWidth(col.header));

      const isCashPayment = method => {
        if (!method) return false;
        const m = method.toUpperCase();
        return m === 'CASH' || m.includes('현금');
      };

      const isTransferPayment = method => {
        if (!method) return false;
        const m = method.toUpperCase();
        return m.includes('계좌') || m.includes('TRANSFER');
      };

      const isCardPayment = method => {
        if (!method) return false;
        const m = method.toUpperCase();
        return m === 'CARD' || m.includes('카드') || m.includes('CREDIT');
      };

      // 수단별 금액 — 서버가 결제 라인 기준으로 쪼갠 값(cashAmount/transferAmount/cardAmount)을 쓴다.
      // 분할결제(PAYMENT_METHOD='MIXED')는 수단명으로는 어느 칸에도 안 걸려 세 칸 모두 0 이 되던 문제.
      // 값이 없는 행만 예전처럼 수단명으로 판정한다.
      const methodAmount = (splitAmount, matchesMethod, row) =>
        splitAmount != null ? Number(splitAmount) || 0 : matchesMethod ? row.finalPrice || 0 : 0;

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell, colNumber) => {
        cell.fill = headerStyle.fill;
        cell.font = headerStyle.font;
        cell.border = headerStyle.border;
        cell.alignment = headerStyle.alignment;
      });

      let currentRow = 2;
      const data = allData;

      const {
        ticketSales,
        passSales,
        productSales,
        ptSales,
        pilatesSales,
        trialSales,
        ticketRefunds,
        passRefunds,
        productRefunds,
        ptRefunds,
        pilatesRefunds,
        trialRefunds,
      } = aggregateCategoryTotals(data);

      data.forEach((row, index) => {
        const payType = row.payType || row.payTypeName || '';
        const isRefundRow = row.rowType === 'REFUND';

        const paymentMethod = row.paymentMethod || '';
        const cellValues = [
          row.payTypeName || payType,
          row.memberName || '(삭제회원)',
          row.paidDate || '',
          formatProductName(row),
          isRefundRow ? '환불' : Number(row.isNew) === 1 ? '신규' : '재등록',
          row.paidDate?.split(' ')[0] || '',
          isRefundRow ? 0 : methodAmount(row.cashAmount, isCashPayment(paymentMethod), row),
          isRefundRow ? 0 : methodAmount(row.transferAmount, isTransferPayment(paymentMethod), row),
          isRefundRow ? 0 : methodAmount(row.cardAmount, isCardPayment(paymentMethod), row),
          isRefundRow ? 0 : row.outstandingAmount || 0,
          row.phoneNumber || '',
          paymentMethod,
          row.paymentProviderName || '',
          row.memo || '',
        ];

        cellValues.forEach((value, idx) => {
          const len = typeof value === 'number' ? value.toLocaleString().length : getTextWidth(value);
          if (len > columnWidths[idx]) columnWidths[idx] = len;
        });

        const rowData = worksheet.addRow({
          payTypeName: cellValues[0],
          memberName: cellValues[1],
          paidDate: cellValues[2],
          courseTicketName: cellValues[3],
          category: cellValues[4],
          regDate: cellValues[5],
          cashAmount: cellValues[6],
          transferAmount: cellValues[7],
          cardAmount: cellValues[8],
          outstandingAmount: cellValues[9],
          phoneNumber: cellValues[10],
          paymentMethod: cellValues[11],
          paymentProviderName: cellValues[12],
          memo: cellValues[13],
        });

        const bgColor = index % 2 === 0 ? rowColors.odd : rowColors.even;
        rowData.eachCell((cell, colNumber) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
          cell.border = cellBorder;
          cell.font = { size: 12 };

          if ([7, 8, 9, 10].includes(colNumber)) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
            cell.numFmt = '#,##0';
          } else {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          }
        });

        currentRow++;
      });

      worksheet.columns.forEach((column, idx) => {
        const computed = columnWidths[idx] + 2;
        column.width = Math.min(Math.max(computed, 6), 50);
      });

      const totalSales = exportPaymentTotal?.totalPaidAmount || 0;

      const totalRefunds = exportPaymentTotal?.totalRefundAmount || 0;

      const yellowBg = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
      const greenBg = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } };

      const summaryTopRow = currentRow + 2;

      const redBg = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCE4E4' } };
      const refundFont = { size: 12, color: { argb: 'FFCC0000' } };

      const renderSummaryBlock = ({
        startCol,
        title,
        dateLabel,
        totalLabel,
        totalValue,
        items,
        refundItems,
        refundTotal,
      }) => {
        const colLabel = startCol;
        const colGap = startCol + 1;
        const colValue = startCol + 2;
        let row = summaryTopRow;

        worksheet.mergeCells(row, colLabel, row, colValue);
        const dateCell = worksheet.getCell(row, colLabel);
        dateCell.value = dateLabel;
        dateCell.font = { bold: true, size: 12 };
        dateCell.border = cellBorder;
        dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
        row++;

        worksheet.mergeCells(row, colLabel, row, colGap);
        const titleCell = worksheet.getCell(row, colLabel);
        titleCell.value = title;
        titleCell.fill = yellowBg;
        titleCell.font = { bold: true, size: 12 };
        titleCell.border = cellBorder;
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

        const titleAmount = worksheet.getCell(row, colValue);
        titleAmount.value = totalValue;
        titleAmount.numFmt = '₩ #,##0';
        titleAmount.fill = yellowBg;
        titleAmount.font = { bold: true, size: 12 };
        titleAmount.border = cellBorder;
        titleAmount.alignment = { horizontal: 'right', vertical: 'middle' };
        row++;

        items.forEach(item => {
          worksheet.mergeCells(row, colLabel, row, colGap);
          const label = worksheet.getCell(row, colLabel);
          label.value = item.label;
          label.fill = greenBg;
          label.font = { size: 12 };
          label.border = cellBorder;
          label.alignment = { horizontal: 'left', vertical: 'middle' };

          const amount = worksheet.getCell(row, colValue);
          amount.value = item.value;
          amount.numFmt = '₩ #,##0';
          amount.fill = greenBg;
          amount.font = { size: 12 };
          amount.border = cellBorder;
          amount.alignment = { horizontal: 'right', vertical: 'middle' };
          row++;
        });

        worksheet.mergeCells(row, colLabel, row, colGap);
        const refundHeader = worksheet.getCell(row, colLabel);
        refundHeader.value = '환불';
        refundHeader.fill = redBg;
        refundHeader.font = { bold: true, size: 12, color: { argb: 'FFCC0000' } };
        refundHeader.border = cellBorder;
        refundHeader.alignment = { horizontal: 'center', vertical: 'middle' };

        const refundHeaderAmount = worksheet.getCell(row, colValue);
        refundHeaderAmount.value = refundTotal > 0 ? -refundTotal : 0;
        refundHeaderAmount.numFmt = '₩ #,##0;[Red]₩ -#,##0';
        refundHeaderAmount.fill = redBg;
        refundHeaderAmount.font = { bold: true, size: 12, color: { argb: 'FFCC0000' } };
        refundHeaderAmount.border = cellBorder;
        refundHeaderAmount.alignment = { horizontal: 'right', vertical: 'middle' };
        row++;

        refundItems.forEach(item => {
          worksheet.mergeCells(row, colLabel, row, colGap);
          const label = worksheet.getCell(row, colLabel);
          label.value = item.label;
          label.fill = redBg;
          label.font = refundFont;
          label.border = cellBorder;
          label.alignment = { horizontal: 'left', vertical: 'middle' };

          const amount = worksheet.getCell(row, colValue);
          amount.value = item.value > 0 ? -item.value : 0;
          amount.numFmt = '₩ #,##0;[Red]₩ -#,##0';
          amount.fill = redBg;
          amount.font = refundFont;
          amount.border = cellBorder;
          amount.alignment = { horizontal: 'right', vertical: 'middle' };
          row++;
        });

        worksheet.mergeCells(row, colLabel, row, colGap);
        const netLabel = worksheet.getCell(row, colLabel);
        netLabel.value = totalLabel;
        netLabel.fill = yellowBg;
        netLabel.font = { bold: true, size: 12 };
        netLabel.border = cellBorder;
        netLabel.alignment = { horizontal: 'center', vertical: 'middle' };

        const netAmount = worksheet.getCell(row, colValue);
        netAmount.value = totalValue - refundTotal;
        netAmount.numFmt = '₩ #,##0';
        netAmount.fill = yellowBg;
        netAmount.font = { bold: true, size: 12 };
        netAmount.border = cellBorder;
        netAmount.alignment = { horizontal: 'right', vertical: 'middle' };

        const ensureMinWidth = (colIdx, minWidth) => {
          const col = worksheet.getColumn(colIdx);
          if (!col.width || col.width < minWidth) col.width = minWidth;
        };
        ensureMinWidth(colLabel, 26);
        ensureMinWidth(colGap, 16);
        ensureMinWidth(colValue, 18);
      };

      renderSummaryBlock({
        startCol: 1,
        title: isSingleDay ? '일일 영업 매출' : '기간 영업 매출',
        dateLabel: isSingleDay
          ? moment(periodFromDate).format('YYYY년 M월 D일 dddd')
          : `${periodFromDate} ~ ${periodToDate}`,
        totalLabel: isSingleDay ? '일일 순매출 [ 합 계 ]' : '기간 순매출 [ 합 계 ]',
        totalValue: totalSales,
        items: [
          { label: '* 회원권 매출 (옵션포함)', value: ticketSales },
          { label: '* 이용권 매출', value: passSales },
          { label: '* PT 매출 (개인레슨)', value: ptSales },
          { label: '* 필라테스 매출 (개인/그룹)', value: pilatesSales },
          { label: '* 일일권 매출', value: trialSales },
          { label: '* 상품 매출 (음료수등)', value: productSales },
        ],
        refundItems: [
          {
            label: '· 회원권 환불 (수강권/이용권/일일권 포함)',
            value: ticketRefunds + passRefunds + ptRefunds + pilatesRefunds + trialRefunds,
          },
          { label: '· 상품 환불', value: productRefunds },
        ],
        refundTotal: totalRefunds,
      });

      // 조회 기간이 여러 달에 걸치면 오른쪽에 월별 순매출 표를 붙인다.
      // 단일 월(일일/주간/월간 조회)이면 왼쪽 블록과 같은 값이라 생략한다.
      const renderMonthlyTable = startCol => {
        const monthlyMap = {};
        data.forEach(row => {
          if (!row.paidDate) return;
          const key = moment(row.paidDate).format('YYYY-MM');
          if (!monthlyMap[key]) monthlyMap[key] = { sales: 0, refunds: 0 };
          if (row.rowType === 'REFUND') monthlyMap[key].refunds += Math.abs(row.refundAmount || 0);
          else monthlyMap[key].sales += row.finalPrice || 0;
        });

        // 결제가 없는 달도 0으로 채워 기간 전체를 보여준다.
        const months = [];
        const cursor = moment(periodFromDate).startOf('month');
        const last = moment(periodToDate).startOf('month');
        while (cursor.isSameOrBefore(last)) {
          months.push(cursor.format('YYYY-MM'));
          cursor.add(1, 'month');
        }

        const headers = ['월', '매출', '환불', '순매출'];
        const lastCol = startCol + headers.length - 1;
        let row = summaryTopRow;

        worksheet.mergeCells(row, startCol, row, lastCol);
        const titleCell = worksheet.getCell(row, startCol);
        titleCell.value = '월별 순매출';
        titleCell.fill = yellowBg;
        titleCell.font = { bold: true, size: 12 };
        titleCell.border = cellBorder;
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        row++;

        headers.forEach((label, idx) => {
          const cell = worksheet.getCell(row, startCol + idx);
          cell.value = label;
          cell.fill = headerStyle.fill;
          cell.font = headerStyle.font;
          cell.border = cellBorder;
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        row++;

        let salesSum = 0;
        let refundSum = 0;
        months.forEach(month => {
          const { sales, refunds } = monthlyMap[month] || { sales: 0, refunds: 0 };
          salesSum += sales;
          refundSum += refunds;
          const values = [
            moment(month, 'YYYY-MM').format('YYYY년 M월'),
            sales,
            refunds > 0 ? -refunds : 0,
            sales - refunds,
          ];
          values.forEach((value, idx) => {
            const cell = worksheet.getCell(row, startCol + idx);
            cell.value = value;
            cell.fill = greenBg;
            cell.font = idx === 2 && refunds > 0 ? refundFont : { size: 12 };
            cell.border = cellBorder;
            cell.numFmt = idx === 0 ? undefined : '₩ #,##0;[Red]₩ -#,##0';
            cell.alignment = { horizontal: idx === 0 ? 'center' : 'right', vertical: 'middle' };
          });
          row++;
        });

        const totals = ['합 계', salesSum, refundSum > 0 ? -refundSum : 0, salesSum - refundSum];
        totals.forEach((value, idx) => {
          const cell = worksheet.getCell(row, startCol + idx);
          cell.value = value;
          cell.fill = yellowBg;
          cell.font = { bold: true, size: 12 };
          cell.border = cellBorder;
          cell.numFmt = idx === 0 ? undefined : '₩ #,##0;[Red]₩ -#,##0';
          cell.alignment = { horizontal: idx === 0 ? 'center' : 'right', vertical: 'middle' };
        });

        headers.forEach((label, idx) => {
          const col = worksheet.getColumn(startCol + idx);
          const minWidth = idx === 0 ? 14 : 18;
          if (!col.width || col.width < minWidth) col.width = minWidth;
        });
      };

      // 우측 블록: 그 달 일부만 조회했으면 '당월 합계', 여러 달이면 월별 순매출 표,
      // 그 달 전체(월간 조회)면 좌측과 값이 같으므로 아무것도 그리지 않는다.
      if (needsMonthlyBlock) {
        const monthlyTotals = aggregateCategoryTotals(monthlyData);
        renderSummaryBlock({
          startCol: 5,
          title: '당월 합계',
          dateLabel: `${moment(periodFromDate).format('YYYY년 M월')} 기준`,
          totalLabel: '당월 순매출 [ 합 계 ]',
          totalValue: monthlyPaymentTotal?.totalPaidAmount || 0,
          items: [
            { label: '* 회원권 매출 (옵션포함)', value: monthlyTotals.ticketSales },
            { label: '* 이용권 매출', value: monthlyTotals.passSales },
            { label: '* PT 매출 (개인레슨)', value: monthlyTotals.ptSales },
            { label: '* 필라테스 매출 (개인/그룹)', value: monthlyTotals.pilatesSales },
            { label: '* 일일권 매출', value: monthlyTotals.trialSales },
            { label: '* 상품 매출 (음료수등)', value: monthlyTotals.productSales },
          ],
          refundItems: [
            {
              label: '· 회원권 환불 (수강권/이용권/일일권 포함)',
              value:
                monthlyTotals.ticketRefunds +
                monthlyTotals.passRefunds +
                monthlyTotals.ptRefunds +
                monthlyTotals.pilatesRefunds +
                monthlyTotals.trialRefunds,
            },
            { label: '· 상품 환불', value: monthlyTotals.productRefunds },
          ],
          refundTotal: monthlyPaymentTotal?.totalRefundAmount || 0,
        });
      } else if (!isSingleMonth) {
        renderMonthlyTable(5);
      }

      // ── 성별·나이대별 순매출 통계 시트 ──
      const AGE_BUCKETS = ['10대 이하', '20대', '30대', '40대', '50대', '60대', '70대 이상', '미상'];
      const GENDER_LABELS = ['남', '여', '미상'];

      // 생년월일이 등록일로 채워진 값·1900 더미가 운영에 다수 존재해, 유효 범위를 벗어나면 미상으로 뺀다.
      const getAgeBucket = row => {
        if (!row.birthDate) return '미상';
        const birth = moment(row.birthDate, 'YYYY-MM-DD');
        if (!birth.isValid()) return '미상';
        const birthYear = birth.year();
        if (birthYear < 1930 || birthYear > 2020) return '미상';
        const base = row.paidDate ? moment(row.paidDate) : moment();
        const age = base.diff(birth, 'years');
        if (age < 0) return '미상';
        if (age < 20) return '10대 이하';
        if (age >= 70) return '70대 이상';
        return `${Math.floor(age / 10) * 10}대`;
      };

      const getGenderLabel = row => {
        if (row.gender === 'MALE') return '남';
        if (row.gender === 'FEMALE') return '여';
        return '미상';
      };

      const statsMap = {};
      AGE_BUCKETS.forEach(bucket => {
        statsMap[bucket] = { 남: 0, 여: 0, 미상: 0 };
      });

      data.forEach(row => {
        // 매출은 결제행(PAY), 환불은 환불행(REFUND). 순매출 = 결제 - 환불
        const isRefundRow = row.rowType === 'REFUND';
        const net = isRefundRow ? -Math.abs(row.refundAmount || 0) : row.finalPrice || 0;
        statsMap[getAgeBucket(row)][getGenderLabel(row)] += net;
      });

      const statsSheet = workbook.addWorksheet('통계', {
        pageSetup: {
          orientation: 'portrait',
          paperSize: 9,
          horizontalCentered: true,
          margins: { left: 0.3, right: 0.3, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
        },
      });

      const STATS_LAST_COL = 6;
      const statsWidths = [16, 16, 16, 16, 18, 10];
      statsWidths.forEach((width, idx) => {
        statsSheet.getColumn(idx + 1).width = width;
      });

      statsSheet.mergeCells(1, 1, 1, STATS_LAST_COL);
      const statsTitle = statsSheet.getCell(1, 1);
      statsTitle.value = '성별 · 나이대별 순매출';
      statsTitle.font = { bold: true, size: 14 };
      statsTitle.alignment = { horizontal: 'center', vertical: 'middle' };

      statsSheet.mergeCells(2, 1, 2, STATS_LAST_COL);
      const statsPeriod = statsSheet.getCell(2, 1);
      const periodFrom = appliedQuery.paymentDateFrom || '';
      const periodTo = appliedQuery.paymentDateTo || '';
      statsPeriod.value = periodFrom || periodTo ? `기간 ${periodFrom} ~ ${periodTo}` : '';
      statsPeriod.font = { size: 11, color: { argb: 'FF666666' } };
      statsPeriod.alignment = { horizontal: 'center', vertical: 'middle' };

      const statsHeaderRow = 4;
      ['나이대', '남', '여', '미상', '합계', '비중'].forEach((label, idx) => {
        const cell = statsSheet.getCell(statsHeaderRow, idx + 1);
        cell.value = label;
        cell.fill = headerStyle.fill;
        cell.font = headerStyle.font;
        cell.border = headerStyle.border;
        cell.alignment = headerStyle.alignment;
      });

      const genderTotals = { 남: 0, 여: 0, 미상: 0 };
      let grandTotal = 0;
      AGE_BUCKETS.forEach(bucket => {
        GENDER_LABELS.forEach(gender => {
          genderTotals[gender] += statsMap[bucket][gender];
          grandTotal += statsMap[bucket][gender];
        });
      });

      const toRatio = value => (grandTotal === 0 ? 0 : value / grandTotal);

      let statsRow = statsHeaderRow + 1;
      AGE_BUCKETS.forEach((bucket, idx) => {
        const values = GENDER_LABELS.map(gender => statsMap[bucket][gender]);
        const bucketSum = values.reduce((acc, cur) => acc + cur, 0);
        const cells = [bucket, ...values, bucketSum, toRatio(bucketSum)];

        cells.forEach((value, colIdx) => {
          const cell = statsSheet.getCell(statsRow, colIdx + 1);
          cell.value = value;
          cell.border = cellBorder;
          cell.font = { size: 12 };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: idx % 2 === 0 ? rowColors.odd : rowColors.even },
          };
          if (colIdx === 0) {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          } else {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
            cell.numFmt = colIdx === STATS_LAST_COL - 1 ? '0.0%' : '#,##0';
          }
        });
        statsRow++;
      });

      const totalCells = [
        '합계',
        ...GENDER_LABELS.map(gender => genderTotals[gender]),
        grandTotal,
        grandTotal === 0 ? 0 : 1,
      ];
      totalCells.forEach((value, colIdx) => {
        const cell = statsSheet.getCell(statsRow, colIdx + 1);
        cell.value = value;
        cell.border = cellBorder;
        cell.font = { bold: true, size: 12 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
        if (colIdx === 0) {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.numFmt = colIdx === STATS_LAST_COL - 1 ? '0.0%' : '#,##0';
        }
      });
      statsRow++;

      const ratioCells = ['성별 비중', ...GENDER_LABELS.map(gender => toRatio(genderTotals[gender])), '', ''];
      ratioCells.forEach((value, colIdx) => {
        const cell = statsSheet.getCell(statsRow, colIdx + 1);
        cell.value = value;
        cell.border = cellBorder;
        cell.font = { size: 12 };
        if (colIdx === 0) {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          if (typeof value === 'number') cell.numFmt = '0.0%';
        }
      });
      statsRow += 2;

      // 주석은 길어서 한 줄 높이에 잘린다. 2행을 병합해 두 줄로 보이게 한다.
      statsSheet.mergeCells(statsRow, 1, statsRow + 1, STATS_LAST_COL);
      const statsNote = statsSheet.getCell(statsRow, 1);
      statsNote.value =
        '※ 순매출 = 결제금액 - 환불금액 (결제일 기준). 나이는 결제일 기준 만나이이며, 생년월일이 없거나 비정상인 회원은 미상으로 집계됩니다.';
      statsNote.font = { size: 10, color: { argb: 'FF666666' } };
      statsNote.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      statsRow += 1;

      // ── 방문경로별 순매출 ──
      // 회원의 현재 방문경로를 조회 시점에 읽는다. 나중에 회원 상세에서 값을 채우면
      // 그 회원의 과거 결제도 해당 경로로 잡힌다(의도된 동작).
      const pathStats = {};
      data.forEach(row => {
        const key = VISIT_PATH_LABELS[row.visitPath] ? row.visitPath : 'UNKNOWN';
        if (!pathStats[key]) pathStats[key] = { net: 0, count: 0 };
        const isRefundRow = row.rowType === 'REFUND';
        pathStats[key].net += isRefundRow ? -Math.abs(row.refundAmount || 0) : row.finalPrice || 0;
        pathStats[key].count += 1;
      });

      // 결제가 한 건도 없는 경로는 줄만 늘어나므로 뺀다. 미상은 항상 남긴다.
      const pathRows = [
        ...VISIT_PATH_OPTIONS.filter(option => pathStats[option.value]).map(option => ({
          label: option.label,
          net: pathStats[option.value].net,
        })),
        { label: '미상', net: pathStats.UNKNOWN ? pathStats.UNKNOWN.net : 0 },
      ];
      const pathTotal = pathRows.reduce((acc, cur) => acc + cur.net, 0);
      const toPathRatio = value => (pathTotal === 0 ? 0 : value / pathTotal);

      statsRow += 2;
      const pathLastCol = 3;
      statsSheet.mergeCells(statsRow, 1, statsRow, pathLastCol);
      const pathTitle = statsSheet.getCell(statsRow, 1);
      pathTitle.value = '방문경로별 순매출';
      pathTitle.font = { bold: true, size: 14 };
      pathTitle.alignment = { horizontal: 'center', vertical: 'middle' };
      statsRow += 2;

      ['방문경로', '순매출', '비중'].forEach((label, idx) => {
        const cell = statsSheet.getCell(statsRow, idx + 1);
        cell.value = label;
        cell.fill = headerStyle.fill;
        cell.font = headerStyle.font;
        cell.border = headerStyle.border;
        cell.alignment = headerStyle.alignment;
      });
      statsRow++;

      pathRows.forEach((row, idx) => {
        [row.label, row.net, toPathRatio(row.net)].forEach((value, colIdx) => {
          const cell = statsSheet.getCell(statsRow, colIdx + 1);
          cell.value = value;
          cell.border = cellBorder;
          cell.font = { size: 12 };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: idx % 2 === 0 ? rowColors.odd : rowColors.even },
          };
          if (colIdx === 0) {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          } else {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
            cell.numFmt = colIdx === pathLastCol - 1 ? '0.0%' : '#,##0';
          }
        });
        statsRow++;
      });

      ['합계', pathTotal, pathTotal === 0 ? 0 : 1].forEach((value, colIdx) => {
        const cell = statsSheet.getCell(statsRow, colIdx + 1);
        cell.value = value;
        cell.border = cellBorder;
        cell.font = { bold: true, size: 12 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
        if (colIdx === 0) {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.numFmt = colIdx === pathLastCol - 1 ? '0.0%' : '#,##0';
        }
      });
      statsRow += 2;

      statsSheet.mergeCells(statsRow, 1, statsRow + 1, STATS_LAST_COL);
      const pathNote = statsSheet.getCell(statsRow, 1);
      pathNote.value =
        '※ 방문경로는 잠재회원에서 전환될 때 승계되거나 회원 상세 모달에서 직접 지정합니다. 값이 없는 회원은 미상으로 집계됩니다.';
      pathNote.font = { size: 10, color: { argb: 'FF666666' } };
      pathNote.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `매출현황_${moment().format('YYYY-MM-DD')}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success(`엑셀 다운로드 완료 (총 ${allData.length}건)`);
    } finally {
      setExcelLoading(false);
    }
  };

  // 최초 진입 + page/limit 변경 시 조회
  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      const updatedQuery = {
        ...query,
        headBranchId: user?.headBranch?.headBranchId,
        branchId: user?.branch?.branchId,
      };
      setQuery(updatedQuery);
      fetchData(updatedQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, query.limit, query.page]);

  if (loading) {
    return (
      <LoadingScreen>
        <CircularProgress />
      </LoadingScreen>
    );
  }

  return (
    <PageContainer>
      <SalesManagPeriodBar
        tab={periodTab}
        custom={customRange}
        onTabChange={handlePeriodTabChange}
        refDate={refDate}
        onRefDateChange={handleRefDateChange}
        dateFrom={query.paymentDateFrom}
        dateTo={query.paymentDateTo}
        onRangeChange={handleRangeChange}
      />

      <FilterDashboardRow>
        <SalesManagFilterPanel
          filters={filters}
          appliedFilters={appliedFilters}
          onFiltersChange={setFilters}
          onSearch={handleSearch}
          onReset={handleReset}
          onRemoveToken={handleRemoveToken}
          loading={tableLoading}
        />
        <SalesManagDashboard
          paymentTotal={paymentTotal}
          totalCount={dashboardCount}
          transferFeeTotal={transferFeeTotal}
          periodTab={periodTab}
          refDate={refDate}
        />
      </FilterDashboardRow>

      <SalesManagDataTable
        payments={payments}
        summary={summary}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        limit={query.limit}
        onLimitChange={changeLimit}
        page={query.page}
        onPageChange={handleChangePage}
        onExcel={exportToExcel}
        excelLoading={excelLoading}
        tableLoading={tableLoading}
        onNavigateMember={memberId => navigate(`/member/${memberId}?tab=four`)}
        onClickCancel={handleCancelClick}
        onClickDetail={handleDetailClick}
        onClickRefundReversal={handleRefundReversalClick}
        canPaymentDetailEdit={canPaymentDetailEdit}
        canContractEdit={canContractEdit}
        onFetchGroupDetail={handleFetchGroupDetail}
        allExpansion={allTabExpansion}
      />

      <ManualRefundModalV2
        ticketData={selectedTicketData}
        open={isModalOpen}
        onClose={handleModalClose}
        fetchData={() => getPaymentListEvent()}
      />
      <ManualDetailModalV2
        ticketData={selectedTicketData}
        open={isDetailModalOpen}
        onClose={handleDetailModalClose}
        readOnly={false}
        fetchData={() => getPaymentListEvent()}
      />

      {/* 환불취소 확인 — 환불 행 '환불취소' 버튼. 사유는 선택 입력. */}
      <Dialog
        open={!!reversalTarget}
        onClose={reversalSubmitting ? undefined : handleReversalClose}
        maxWidth="xs"
        fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 700 }}>환불취소</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '13px', mb: '12px' }}>
            {`${reversalTarget?.courseTicketName || ''} 환불을 취소하고 결제 상태로 되돌리시겠습니까?`}
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
            placeholder="환불취소 사유 (선택)"
            value={reversalReason}
            onChange={e => setReversalReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReversalClose} disabled={reversalSubmitting}>
            취소
          </Button>
          <Button variant="contained" onClick={handleReversalConfirm} disabled={reversalSubmitting}>
            {reversalSubmitting ? '처리 중…' : '환불취소'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 상품등록 모달 (자체 트리거는 비활성 상태로 기존과 동일하게 유지) */}
      <ProductModalV2 fetchData={() => fetchData(query)} />
    </PageContainer>
  );
};

export default SalesManagTab1;
